/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFormik } from "formik";
import { trackAssetFormOutcome } from "../../analytics";
import * as Yup from "yup";
import { IAssetDetailsResponse, IAssetInfo, IAssetTypesResponse } from "../../models/asset";
import { useMutation, useQuery } from "@tanstack/react-query";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import { ASSET_MIN_PURCHASE_DATE, yupAssetPurchaseDate } from "../../commons/assetPurchaseDate";
import useAuthHttp from "../../hooks/useAuthHttp";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import Nav, { NavItem } from "../Nav";
import View from "../Container";
import Text from "../Text";
import Button from "../Button";
import FormField from "../FormField";
import { InputNumber } from "primereact/inputnumber";
import BankSearchSelector from "../BankSearchSelector";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import Label from "../Label";
import { useHomeContext } from "../../contexts/HomeContext";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

export interface ICheckingAccountHandleFormProps {
    onSuccessRegister(assetInfo: ICheckingAccountHandleForm | null): void;
    assetId?: string;
    goBack(): void;
    initialData?: IExtractedInvestment;
}

export interface ICheckingAccountHandleForm {
    startDate?: Date;
    negotiatingBank?: {
        id: string;
        name: string;
    };
    amount: number;
    withIndexCorrelation: boolean;
    indexCorrelation?: number;
    operationCosts: number;
}

const CheckingAccoundHandleForm = (props: ICheckingAccountHandleFormProps) => {
    const [isEdit, setIsEdit] = useState(false);
    const submittingRef = useRef(false);
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();
    const formik = useFormik<ICheckingAccountHandleForm>({
        initialValues: {
            amount: 0,
            negotiatingBank: undefined,
            startDate: undefined,
            indexCorrelation: undefined,
            withIndexCorrelation: false,
            operationCosts: 0
        },
        validationSchema: Yup.object().shape({
            amount: Yup.number()
                .min(10, "Valor mínimo obrigatório")
                .required('Campo obrigatório'),
            negotiatingBank: Yup.object()
                .required("Campo obrigatório"),
            startDate: yupAssetPurchaseDate(),
            indexCorrelation: Yup.number()
                .when("withIndexCorrelation", {
                    is: true,
                    then: (schema) => schema
                        .min(1, "Valor mínimo obrigatório")
                        .required("Campo obrigatório"),
                    otherwise: (schema) => schema.nullable().optional()
                }),
            withIndexCorrelation: Yup
                .boolean()
                .optional(),
            operationCosts: Yup
                .number()
                .nullable()
                .optional()
        }),
        onSubmit: (values) => {
            send(values).then();
        }
    });

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const http = useAuthHttp();
    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                return {
                    asset_info: null
                };
            }

            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    const insertAssetMutation = useMutation({
        mutationFn: async (form: ICheckingAccountHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped.find(listType => listType.groupName == "Conta Corrente")!.names[0].id!,
                asset_type: "CA",
                asset_fin_negotiating_id: form.negotiatingBank!.id,
                asset_purchase_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_ca_details: {
                    ca_rate_cdi: form.indexCorrelation ?? 0,
                    ca_monthly_costs: form.operationCosts
                },
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: form.amount
                },
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {})
            };

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const updateAssetMutation = useMutation({
        mutationFn: async (form: ICheckingAccountHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped.find(listType => listType.groupName == "Conta Corrente")!.names[0].id!,
                asset_type: "CA",
                asset_fin_negotiating_id: form.negotiatingBank!.id,
                asset_purchase_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_ca_details: {
                    ca_rate_cdi: form.indexCorrelation ?? 0,
                    ca_monthly_costs: form.operationCosts
                },
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: form.amount
                }
            };

            return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
        }
    });

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            setIsEdit(false);
            return;
        }

        setIsEdit(true);

        const eventUser = assetDetailsQuery.data?.asset_info.asset_event_user.find(a => a.event_type == "CHARGE");
        formik.setValues({
            startDate: moment(eventUser?.event_date).toDate(),
            negotiatingBank: {
                id: assetDetailsQuery.data.asset_info.asset_fin_issue.id,
                name: assetDetailsQuery.data.asset_info.asset_fin_issue.inst_name
            },
            amount: eventUser?.event_value ?? 0,
            withIndexCorrelation: !!assetDetailsQuery.data.asset_info.asset_ca_details.ca_rate_cdi,
            indexCorrelation: assetDetailsQuery.data.asset_info.asset_ca_details.ca_rate_cdi,
            operationCosts: assetDetailsQuery.data.asset_info.asset_ca_details.ca_rate_cdi
        });
    }, [assetDetailsQuery.data?.asset_info]);

    useEffect(() => {
        if (!props.initialData?.asset_ca_details) {
            return;
        }
        const data = props.initialData;
        formik.setValues({
            amount: data.asset_current_value || 0,
            startDate: data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined,
            withIndexCorrelation: (data.asset_ca_details?.ca_rate_cdi || 0) > 0,
            indexCorrelation: data.asset_ca_details?.ca_rate_cdi || 0,
            operationCosts: data.asset_ca_details?.ca_monthly_costs || 0,
            negotiatingBank: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const upsert = async (assetPayload: ICheckingAccountHandleForm) => {
        if (isEdit) return await updateAssetMutation.mutateAsync(assetPayload);

        return await insertAssetMutation.mutateAsync(assetPayload);
    }

    const send = async (form: ICheckingAccountHandleForm) => {
        if (!form || submittingRef.current || insertAssetMutation.isPending) {
            return Promise.resolve();
        }
        submittingRef.current = true;

        try {
            const assetInfo = await upsert(form);

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                if (!isEdit) trackAssetFormOutcome('checking_account', false, 'failed');
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            if (!isEdit) trackAssetFormOutcome('checking_account', false, 'failed');
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        } finally {
            submittingRef.current = false;
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Conta Corrente</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <FormField
                        id="amount"
                        hasError={!!formik.errors.amount && formik.touched.amount}
                        error={formik.errors.amount}
                        label="Valor"
                    >
                        <InputNumber
                            invalid={!!formik.errors.amount && formik.touched.amount}
                            value={formik.values.amount}
                            onValueChange={(e) =>
                                formik.setFieldValue("amount", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <BankSearchSelector
                        hasError={!!formik.errors.negotiatingBank && formik.touched.negotiatingBank}
                        error={formik.errors.negotiatingBank}
                        selected={formik.values.negotiatingBank}
                        label="Qual é o banco?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("negotiatingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <FormField
                        id="startDate"
                        label="Data de início?"
                        hasError={!!formik.errors.startDate && formik.touched.startDate}
                        error={formik.errors.startDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.startDate && formik.touched.startDate}
                            value={formik.values.startDate}
                            dateFormat="dd/mm/yy"
                            showIcon
                            readOnlyInput
                            minDate={ASSET_MIN_PURCHASE_DATE}
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("startDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <View className="align-items-center">
                        <Checkbox
                            inputId="withIndexCorrelation"
                            onChange={() => {
                                formik.setFieldValue("withIndexCorrelation", !formik.values.withIndexCorrelation);
                            }}
                            checked={!!formik.values.withIndexCorrelation}
                        />
                        <Label htmlFor="withIndexCorrelation" className="ml-2">Possui rendimento mensal</Label>
                    </View>

                    {
                        formik.values.withIndexCorrelation && (
                            <FormField
                                id="indexCorrelation"
                                hasError={!!formik.errors.indexCorrelation && formik.touched.indexCorrelation}
                                error={formik.errors.indexCorrelation}
                                label="Rendimento mensal (%)"
                            >
                                <InputNumber
                                    className="w-full"
                                    invalid={!!formik.errors.indexCorrelation && formik.touched.indexCorrelation}
                                    value={formik.values.indexCorrelation}
                                    onValueChange={(e) => {
                                        formik.setFieldValue("indexCorrelation", e.value);
                                    }}
                                    suffix="% do CDI"
                                    maxFractionDigits={2}
                                    min={0}
                                    max={200}
                                />
                            </FormField>
                        )
                    }

                    <View flexColumn gap={2}>
                        <Text>Custos mensal da tarifa (opcional)</Text>
                        <InputNumber
                            value={formik.values.operationCosts}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={insertAssetMutation.isPending}
                            disabled={insertAssetMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default CheckingAccoundHandleForm;